// src/features/rooms/pages/RoomDetailsPage/components/RoomEquipmentList.jsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  Stack,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  NoteAdd as NoteAddIcon,
  ViewModule as ViewModuleIcon,
  FileCopy,
} from '@mui/icons-material';
import { getEquipmentImageByModel } from '../../../../equipment/utils/getEquipmentImage';
import EquipmentStatusChip from '../../../../equipment/components/EquipmentStatusChip';

export default function RoomEquipmentList({ 
  roomEquipment, 
  canCreateEquipment, 
  onViewEquipment,
  onAddFromTemplate,
  onAddFromScratch 
}) {
  const { t } = useTranslation(['equipment']);
  const [equipmentMenuAnchor, setEquipmentMenuAnchor] = useState(null);

  const handleOpenEquipmentMenu = useCallback((event) => {
    setEquipmentMenuAnchor(event.currentTarget);
  }, []);

  const handleCloseEquipmentMenu = useCallback(() => {
    setEquipmentMenuAnchor(null);
  }, []);

  const handleAddFromTemplate = useCallback(() => {
    handleCloseEquipmentMenu();
    onAddFromTemplate();
  }, [onAddFromTemplate, handleCloseEquipmentMenu]);

  const handleAddFromScratch = useCallback(() => {
    handleCloseEquipmentMenu();
    onAddFromScratch();
  }, [onAddFromScratch, handleCloseEquipmentMenu]);

  return (
    <Card sx={{ height: "100%", minWidth: "250px" }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">
            {t('equipment:title')} ({roomEquipment.length})
          </Typography>
          {canCreateEquipment && (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={handleOpenEquipmentMenu}
              >
                {t('equipment:actions.add_equipment')}
              </Button>

              <Menu
                anchorEl={equipmentMenuAnchor}
                open={Boolean(equipmentMenuAnchor)}
                onClose={handleCloseEquipmentMenu}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleAddFromTemplate}>
                  <ListItemIcon>
                    <FileCopy fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {t('equipment:actions.add_from_template')}
                  </ListItemText>
                </MenuItem>

                <MenuItem onClick={handleAddFromScratch}>
                  <ListItemIcon>
                    <NoteAddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {t('equipment:actions.add_from_scratch')}
                  </ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
        <Divider sx={{ my: 2 }} />

        {roomEquipment.length === 0 ? (
          <Alert severity="info">
            {t('equipment:messages.no_equipment_in_room')}
          </Alert>
        ) : (
          <Stack spacing={1}>
            {roomEquipment.map((equipment) => (
              <Box
                key={equipment.equipment_id}
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => onViewEquipment(equipment.equipment_id)}
              >
                <Box display="flex" alignItems="center">
                  {(getEquipmentImageByModel(equipment.model) || true) ? (
                    <Box
                      sx={{
                        width: '70px',
                        height: '44px',
                        backgroundImage: `url(${getEquipmentImageByModel(equipment.model)})`,
                        backgroundSize: 'contain',
                        backgroundPosition: "30% 50%",
                        backgroundRepeat: 'no-repeat',
                      }}
                    />
                  ) : (
                    <></>
                  )}
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {equipment.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(`equipment_types.${equipment.equipment_type}`)}
                      {equipment.manufacturer && ` • ${equipment.manufacturer}`}
                      {equipment.model && ` • ${equipment.model}`}
                    </Typography>
                  </Box>
                  <Box flex={1} />
                  <EquipmentStatusChip status={equipment.status} />
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}